import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useInvalidateStageWiseOpportunitiesData } from "~/app/sales/opportunities/hooks/useFetchStageWiseOpportunities";
import { useInvalidateOpportunitiesData } from "~/app/sales/opportunities/hooks/useOpportunitiesData";

import { axios } from "~/core/axios";
import { BackendApiResponse } from "~/core/types/backend-api-response.type";

import { useInvalidateObjectOpportunities } from "~/components/RightSidebarOpportunities/hooks/useObjectOpportunities";

import { OpportunityCreateResponse, OpportunityFormValues } from "../types";

import { useInvalidatePipelinesLastOpportunityCreatedQuery } from "./usePipelinesLastOpportunityCreatedQuery";

type CreateOpportunityMutation = {
  onSuccess: (
    opportunity: OpportunityCreateResponse,
    opportunityData: OpportunityFormValues,
  ) => void;
  onError: (error: Error) => void;
};

export const useCreateOpportunityMutation = ({
  onSuccess,
  onError,
}: CreateOpportunityMutation) => {
  const invalidateOpportunitiesData = useInvalidateOpportunitiesData();
  const invalidateStageWiseOpportunitiesData =
    useInvalidateStageWiseOpportunitiesData();
  const invalidateObjectOpportunities = useInvalidateObjectOpportunities();
  const invalidatePipelinesLastOpportunityCreatedQuery =
    useInvalidatePipelinesLastOpportunityCreatedQuery();

  const { mutateAsync: createOpportunity } = useMutation({
    mutationFn: async (opportunityData: OpportunityFormValues) => {
      try {
        const { data } = await axios.post<
          BackendApiResponse<OpportunityCreateResponse>
        >("/opportunities/new", opportunityData);
        return data.data;
      } catch (error) {
        onError(error as Error);
      }
    },
    onSuccess: (opportunity, variables) => {
      if (!opportunity) {
        onError(new Error("Opportunity not found"));
        return;
      }

      invalidateOpportunitiesData();
      invalidateStageWiseOpportunitiesData();
      invalidateObjectOpportunities();
      invalidatePipelinesLastOpportunityCreatedQuery();

      onSuccess(opportunity, variables);
    },
    onError: error => {
      toast.error("Error creating opportunity");
    },
  });

  return {
    createOpportunity,
  };
};
